import React, { FC, useEffect, useState } from 'react'
import styles from '../../SearchProvider.module.scss'
import InputLabel from 'components/SA/FormElements/InputLabel'
import { getHrefFunction, updateClientSideUrl } from '../../helpers/url'
import { validateState } from '../../helpers/sizeFacet'
import { SASelectedFacets } from 'types/SearchProvider'
import { CustomHandleUpdateSelectedFacetParams, SharedFacetNames } from '../../types'

export type DimensionsObject = {
  currentSize: string
  heightMax: number | null | undefined
  heightMin: number | null | undefined
  heightError: boolean
  widthMax: number | null | undefined
  widthMin: number | null | undefined
  widthError: boolean
}

const initialDimensionsState: DimensionsObject = {
  currentSize: 'custom',
  heightMax: null,
  heightMin: null,
  heightError: false,
  widthMax: null,
  widthMin: null,
  widthError: false,
}

type CustomSizeFieldsProps = {
  facetName: string
  baseUrl: string
  nextAppRoute: string
  measurementUnit: 'cm' | 'in'
  sizeMinMax: Record<string, string | number>
  selectedSize?: ReadonlyArray<string>
  selectedFacets: SASelectedFacets
  pathParams: ReadonlyArray<string>
  sharedFacetNames: SharedFacetNames
  basicQueryParams: Record<string, ReadonlyArray<string>>
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customClickFacetOption?: (params: CustomHandleUpdateSelectedFacetParams) => void
}

const CustomSizeFields: FC<CustomSizeFieldsProps> = ({
  facetName,
  baseUrl,
  nextAppRoute,
  measurementUnit,
  sizeMinMax,
  selectedSize,
  selectedFacets,
  pathParams,
  sharedFacetNames,
  basicQueryParams,
  customHandleUpdateSelectedFacet,
  customClickFacetOption,
}) => {
  let timer
  const timerDuration = 1500

  const [dimensionsState, setDimensionsState] = useState({
    ...initialDimensionsState,
    ...sizeMinMax,
  })
  const { heightMin, heightMax, widthMin, widthMax, widthError, heightError } = dimensionsState
  const initialCustomSizeOpenState =
    (Boolean(heightMin) && Boolean(heightMax)) || (Boolean(widthMin) && Boolean(widthMax))
  const [isCustomSizeOpen, setIsCustomSizeOpen] = useState(initialCustomSizeOpenState)

  const handleCustomSizeToggle = () => {
    const { heightMax, heightMin, widthMax, widthMin } = dimensionsState
    const shouldResetValue = !!heightMax || !!heightMin || !!widthMax || !!widthMin
    if (isCustomSizeOpen && shouldResetValue) {
      setDimensionsState(initialDimensionsState)
      const newSelectedFacets = {
        ...selectedFacets,
        ...basicQueryParams,
        [facetName]: [],
        width: [],
        height: [],
        page: [''],
      }

      const href = getHrefFunction({
        facetsAndQueryParams: newSelectedFacets,
        pathParams,
        sharedFacetNames,
      })()
      const newUrl = `${baseUrl}${href}`

      if (Boolean(customHandleUpdateSelectedFacet)) {
        customHandleUpdateSelectedFacet?.({ newSelectedFacets, newUrl, facetName, facetValue: '' })
        return
      }

      customClickFacetOption?.({ newSelectedFacets, newUrl, facetName, facetValue: '' })
      updateClientSideUrl(newUrl, nextAppRoute)
    }
    setIsCustomSizeOpen((prevValue) => !prevValue)
  }

  const validateNumber = (value: string): number => {
    const validNumber = Number(value)
    return isNaN(validNumber) || validNumber < 0 ? 0 : validNumber
  }

  const inputUpdate = (data) => {
    clearTimeout(timer)
    const newState = { ...dimensionsState, ...data, currentSize: 'custom' }
    const errors = validateState(newState)
    setDimensionsState({ ...newState, ...errors })

    if (!errors.widthError && !errors.heightError) {
      timer = setTimeout(() => {
        if (window && window.dataLayer) {
          window.dataLayer.push({
            event: 'filter',
            label: `Size - Custom - Width ${newState.widthMin || ''}-${newState.widthMax ||
              ''} X Height ${newState.heightMin || ''}-${newState.heightMax || ''}`,
          })
        }

        const widthValue =
          (newState.widthMin || 0) >= 0 && newState.widthMax
            ? `${newState.widthMin || 0}-${newState.widthMax}`
            : ''
        const heightValue =
          (newState.heightMin || 0) >= 0 && newState.heightMax
            ? `${newState.heightMin || 0}-${newState.heightMax}`
            : ''

        const newSelectedFacets = {
          ...selectedFacets,
          ...basicQueryParams,
          [facetName]: [],
          width: [widthValue],
          height: [heightValue],
          page: [''],
        }
        const href = getHrefFunction({
          facetsAndQueryParams: newSelectedFacets,
          pathParams,
          sharedFacetNames,
        })()
        const newUrl = `${baseUrl}${href}`

        if (Boolean(customHandleUpdateSelectedFacet)) {
          customHandleUpdateSelectedFacet?.({
            newSelectedFacets,
            newUrl,
            facetName,
            facetValue: '',
          })
          return
        }

        customClickFacetOption?.({ newSelectedFacets, newUrl, facetName, facetValue: '' })
        updateClientSideUrl(newUrl, nextAppRoute)
      }, timerDuration)
    }

    return timer
  }

  const handleSizeError = (widthOrHeight: 'width' | 'height') => {
    if (widthOrHeight === 'width') {
      const isWidthMinNotEmpty = widthMin
      return widthError && isWidthMinNotEmpty && widthMax
    } else {
      const isHeightMinNotEmpty = heightMin
      return heightError && isHeightMinNotEmpty && heightMax
    }
  }

  useEffect(() => {
    if (Array.isArray(selectedSize) && selectedSize?.length > 0) {
      setIsCustomSizeOpen(false)
      setDimensionsState(initialDimensionsState)
    }
  }, [selectedSize])

  return (
    <div className={styles.customSizeContainer}>
      <button onClick={handleCustomSizeToggle}>
        {isCustomSizeOpen ? 'Remove Custom Size' : 'Select Custom Size'}
      </button>
      {isCustomSizeOpen && (
        <div className={styles.sizeInputWrapper}>
          <div className={styles.sizeInputContainer}>
            <InputLabel
              type='text'
              name='wMin'
              value={widthMin || widthMin === 0 ? widthMin : ''}
              title='W Min'
              onChange={(ev) =>
                inputUpdate({
                  widthMin: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('width') ? ' ' : undefined}
            />
            {'-'}
            <InputLabel
              type='text'
              name='wMax'
              value={widthMax || ''}
              title='W Max'
              onChange={(ev) =>
                inputUpdate({
                  widthMax: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('width') ? ' ' : undefined}
            />
            {measurementUnit}
          </div>
          <div className={styles.sizeInputContainer}>
            <InputLabel
              type='text'
              name='hMin'
              value={heightMin || heightMin === 0 ? heightMin : ''}
              title='H Min'
              onChange={(ev) =>
                inputUpdate({
                  heightMin: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('height') ? ' ' : undefined}
            />
            {'-'}
            <InputLabel
              type='text'
              name='hMax'
              value={heightMax || ''}
              title='H Max'
              onChange={(ev) =>
                inputUpdate({
                  heightMax: validateNumber(ev.target.value),
                })
              }
              error={handleSizeError('height') ? ' ' : undefined}
            />
            {measurementUnit}
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomSizeFields
